/**
 * Curated admin “Update notes” — plain-English ship log.
 * Append an entry when shipping parent/admin-visible work. Tag it with the
 * version it ships in (or will ship in). The admin page filters by current /
 * previous / upcoming and by version (v1.7, v1.8, …).
 *
 * Bump UPDATE_NOTES_VERSION minor (1.x → 1.x+1) every time you add released
 * notes. For work that is written up but not shipped yet, set status to
 * `upcoming` (and a future version) without bumping the current version.
 */

export type UpdateNoteKind = 'new' | 'fix' | 'change' | 'repair';

export type UpdateNoteAudience = 'admin' | 'parents' | 'both';

export type UpdateNoteStatus = 'released' | 'upcoming';

export type UpdateNotesScope = 'current' | 'previous' | 'upcoming' | 'all';

export const UPDATE_NOTES_SCOPES: UpdateNotesScope[] = ['current', 'previous', 'upcoming', 'all'];

export type UpdateNote = {
  id: string;
  /** Ship / notable day (YYYY-MM-DD). Upcoming notes can use a planned date. */
  date: string;
  /** Product version this note belongs to, e.g. "1.7". */
  version: string;
  kind: UpdateNoteKind;
  title: string;
  summary: string;
  audience?: UpdateNoteAudience;
  /**
   * Defaults from the version: greater than UPDATE_NOTES_VERSION → upcoming,
   * otherwise released. Set explicitly for a same-version preview.
   */
  status?: UpdateNoteStatus;
};

/**
 * Current released version shown as the default Update notes filter.
 * Bump the minor number whenever released notes are added.
 */
export const UPDATE_NOTES_VERSION = '1.91';

export function formatUpdateNotesVersion(version: string): string {
  const trimmed = version.trim();
  if (!trimmed) return '';
  return /^v/i.test(trimmed) ? `v${trimmed.slice(1)}` : `v${trimmed}`;
}

export function updateNotesVersionLabel(version = UPDATE_NOTES_VERSION): string {
  return `Version ${version.replace(/^v/i, '')}`;
}

export function parseUpdateNotesVersion(version: string): [major: number, minor: number] {
  const [major, minor] = version.replace(/^v/i, '').split('.').map((part) => Number(part));
  return [Number.isFinite(major) ? major : 0, Number.isFinite(minor) ? minor : 0];
}

export function compareUpdateNotesVersions(a: string, b: string): number {
  const [aMajor, aMinor] = parseUpdateNotesVersion(a);
  const [bMajor, bMinor] = parseUpdateNotesVersion(b);
  if (aMajor !== bMajor) return aMajor - bMajor;
  return aMinor - bMinor;
}

export const UPDATE_NOTE_KINDS: UpdateNoteKind[] = ['new', 'fix', 'change', 'repair'];

export function updateNoteKindLabel(kind: UpdateNoteKind): string {
  switch (kind) {
    case 'new':
      return 'New';
    case 'fix':
      return 'Fixes';
    case 'change':
      return 'Changes';
    case 'repair':
      return 'Repairs';
  }
}

export function updateNoteAudienceLabel(audience: UpdateNoteAudience): string {
  switch (audience) {
    case 'admin':
      return 'Admin';
    case 'parents':
      return 'Parents';
    case 'both':
      return 'Admin & parents';
  }
}

export function updateNotesScopeLabel(scope: UpdateNotesScope): string {
  switch (scope) {
    case 'current':
      return 'Current';
    case 'previous':
      return 'Previous';
    case 'upcoming':
      return 'Upcoming';
    case 'all':
      return 'All';
  }
}

export function updateNoteStatus(
  note: Pick<UpdateNote, 'version' | 'status'>,
  current = UPDATE_NOTES_VERSION,
): UpdateNoteStatus {
  if (note.status) return note.status;
  return compareUpdateNotesVersions(note.version, current) > 0 ? 'upcoming' : 'released';
}

function sortUpdateNotes(notes: UpdateNote[]): UpdateNote[] {
  return notes.slice().sort((a, b) => {
    const versionCmp = compareUpdateNotesVersions(b.version, a.version);
    if (versionCmp !== 0) return versionCmp;
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
}

/** Notes in a scope, newest version then newest date first. */
export function updateNotesForScope(
  notes: UpdateNote[],
  scope: UpdateNotesScope,
  current = UPDATE_NOTES_VERSION,
): UpdateNote[] {
  return sortUpdateNotes(
    notes.filter((note) => {
      const status = updateNoteStatus(note, current);
      if (scope === 'all') return true;
      if (scope === 'upcoming') return status === 'upcoming';
      if (scope === 'current') return status === 'released' && note.version === current;
      return status === 'released' && compareUpdateNotesVersions(note.version, current) < 0;
    }),
  );
}

/** Distinct versions in the given notes, newest first. */
export function updateNotesVersions(notes: UpdateNote[]): string[] {
  return [...new Set(notes.map((note) => note.version))].sort((a, b) =>
    compareUpdateNotesVersions(b, a),
  );
}

export function filterUpdateNotes(
  notes: UpdateNote[],
  opts: {
    scope?: UpdateNotesScope;
    version?: string | null;
    kind?: UpdateNoteKind | null;
    current?: string;
  } = {},
): UpdateNote[] {
  const scoped = updateNotesForScope(notes, opts.scope ?? 'current', opts.current);
  return scoped.filter((note) => {
    if (opts.version && note.version !== opts.version.replace(/^v/i, '')) return false;
    if (opts.kind && note.kind !== opts.kind) return false;
    return true;
  });
}

export function groupUpdateNotesByVersion(
  notes: UpdateNote[],
): { version: string; items: UpdateNote[] }[] {
  return updateNotesVersions(notes).map((version) => ({
    version,
    items: notes.filter((note) => note.version === version),
  }));
}

export function parseUpdateNotesScope(value: string | null | undefined): UpdateNotesScope | null {
  if (!value) return null;
  return UPDATE_NOTES_SCOPES.includes(value as UpdateNotesScope) ? (value as UpdateNotesScope) : null;
}

export function parseUpdateNoteKind(value: string | null | undefined): UpdateNoteKind | null {
  if (!value) return null;
  return UPDATE_NOTE_KINDS.includes(value as UpdateNoteKind) ? (value as UpdateNoteKind) : null;
}

/** Start of the day one calendar month before `now` (local). */
export function pastCalendarMonthStart(now = new Date()): Date {
  const start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  start.setHours(0, 0, 0, 0);
  return start;
}

/** Notes dated on or after the past-calendar-month start, newest first. */
export function updateNotesPastMonth(notes: UpdateNote[], now = new Date()): UpdateNote[] {
  const start = pastCalendarMonthStart(now);
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  return notes
    .filter((n) => {
      const d = parseNoteDate(n.date);
      return d >= start && d <= end;
    })
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

function parseNoteDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function release(version: string, notes: Omit<UpdateNote, 'version'>[]): UpdateNote[] {
  return notes.map((note) => ({ ...note, version }));
}

/** Full curated history, newest version first. */
export const UPDATE_NOTES: UpdateNote[] = [
  ...release('1.91', [
    {
      id: '2026-11-03-public-home-title-sitemap',
      date: '2026-11-03',
      kind: 'fix',
      title: 'The public home page names itself',
      summary:
        'The home page now has a title and a short description for search and link previews. A sitemap lists the public pages, including the club site.',
      audience: 'both',
    },
  ]),
  ...release('1.90', [
    {
      id: '2026-11-02-marshal-logo-spelling',
      date: '2026-11-02',
      kind: 'fix',
      title: 'The logo spells Marshal',
      summary:
        'The public logo, the home-screen icon, and the link preview now say Marshal with one L, and For swim clubs. The old mark said Marshall CRM.',
      audience: 'both',
    },
  ]),
  ...release('1.89', [
    {
      id: '2026-10-29-xero-placeholder-email',
      date: '2026-10-29',
      kind: 'fix',
      title: 'Xero keeps the contact email you set',
      summary:
        'Marshal no longer sends a swimmer placeholder address to Xero, and it does not change the email on a contact that already exists. A treasurer fix in Xero stays put.',
      audience: 'admin',
    },
  ]),
  ...release('1.88', [
    {
      id: '2026-10-28-meet-tile-icon',
      date: '2026-10-28',
      kind: 'new',
      title: 'A meet can wear its own tile',
      summary:
        'In Club, Calendar, Meets, each meet can keep the flag, use a pumpkin, trophy, or waves, or put up its own image. A custom image can be an upload or the meet’s flyer. Spookfest stays on the flag until you choose one and save.',
      audience: 'both',
    },
  ]),
  ...release('1.87', [
    {
      id: '2026-10-28-meet-rsvp-bookings',
      date: '2026-10-28',
      kind: 'fix',
      title: 'Spookfest shows, and you can say if you are going',
      summary:
        'A carnival in this week’s bookings was easy to miss when the club had a long meet list. It now stays on its day. Parents tap the green tick or the red cross for each swimmer. Coaches see who is attending and who is not.',
      audience: 'both',
    },
  ]),
  ...release('1.86', [
    {
      id: '2026-10-28-spookfest-week-bookings',
      date: '2026-10-28',
      kind: 'new',
      title: 'Carnivals show in this week’s bookings',
      summary:
        'A meet such as Spookfest now sits on its day in the parent app bookings and in the admin weekly sessions list. The tile uses the meet’s date, the arrive or warm-up time stored on the meet, and the squads it is open to. It is not a training book-in.',
      audience: 'both',
    },
  ]),
  ...release('1.85', [
    {
      id: '2026-10-28-production-health-sha',
      date: '2026-10-28',
      kind: 'fix',
      title: 'Production health shows the live release',
      summary:
        'The production health check now includes the git commit that was deployed. Staging already did this. It does not change memberships, fees, or invoices.',
      audience: 'admin',
    },
    {
      id: '2026-10-28-notice-month-billing',
      date: '2026-10-28',
      kind: 'new',
      title: 'Fees run for one month after notice',
      summary:
        'On a WASC membership, Give notice records the notice date and sets the end date one month later. Squad fees and the Xero link keep running until that end date, then stop. The Xero link still only matches an existing contact. It does not create or edit one. A membership that has not given notice is left as it is.',
      audience: 'admin',
    },
  ]),
  ...release('1.84', [
    {
      id: '2026-10-27-public-squad-pages',
      date: '2026-10-27',
      kind: 'change',
      title: 'A page for each squad on the club website',
      summary:
        'The WASC public site uses navy and teal, with the club logo in the header. Fitness, Junior, Development, and Performance each have a page with that squad’s sessions and a short description. The description is edited in Website under Squad cards. The join page still shows the notice period and the I agree tick.',
      audience: 'both',
    },
  ]),
  ...release('1.83', [
    {
      id: '2026-10-27-notice-period',
      date: '2026-10-27',
      kind: 'new',
      title: 'Agree to the notice period when you join',
      summary:
        'The WASC join page shows the club’s one-month notice sentence next to the squad fees. A family ticks I agree before the registration is saved. The membership keeps the time and the exact sentence. A later change to the sentence does not rewrite an agreement already given. This does not change invoices.',
      audience: 'both',
    },
  ]),
  ...release('1.82', [
    {
      id: '2026-10-26-scroll-to-top',
      date: '2026-10-26',
      kind: 'fix',
      title: 'Pages open at the top',
      summary:
        'Opening a story, a session, a notice, or another page now starts at the top. Back returns you to the place you left. Club news and the training schedule open on their own page instead of bouncing back to the club home.',
      audience: 'both',
    },
  ]),
  ...release('1.81', [
    {
      id: '2026-10-26-hide-sent-notice',
      date: '2026-10-26',
      kind: 'new',
      title: 'Hide or edit a sent parent notice',
      summary:
        'Communications can hide a notice that is already in the parent app, or change its wording. The notice stays on file and leaves the parent feed. No new phone alert is sent. A club API key can list recent notices and hide or edit one when that permission is turned on, and can read or update a meet public story.',
      audience: 'admin',
    },
  ]),
  ...release('1.80', [
    {
      id: '2026-10-26-club-api-keys',
      date: '2026-10-26',
      kind: 'new',
      title: 'Club API keys and meet close times',
      summary:
        'Settings shows API keys. A club admin can create a key for meet deadlines, session times, and parent notices, and can revoke it so it stops at once. The secret is shown only when it is created. Meet entries can close at a clock time in New Zealand, not only at the end of the day.',
      audience: 'admin',
    },
  ]),
  ...release('1.79', [
    {
      id: '2026-10-26-parent-push-coverage',
      date: '2026-10-26',
      kind: 'fix',
      title: 'Parent notices reach more phones',
      summary:
        'The parent home keeps the Add to Home Screen steps until the app is installed, then keeps Enable until this phone is registered. Not now hides that card for the visit only. Post to parent app shows how many parents push can reach before you send, and parents with no phone registered still get the notice by email if they receive club mail.',
      audience: 'both',
    },
  ]),
  ...release('1.78', [
    {
      id: '2026-10-25-public-notices-and-roadmap',
      date: '2026-10-25',
      kind: 'change',
      title: 'Real notices and a roadmap on the public site',
      summary:
        'The public home page shows the same notices the app sends, using a fictional club, and a short list of what is still to come. Coach tools, a nearly full session, programme drafts, and Xero linking are described with the other features. The club tools themselves are unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.77', [
    {
      id: '2026-10-25-colour-bands-real-screens',
      date: '2026-10-25',
      kind: 'change',
      title: 'Colour bands and real screens on the public site',
      summary:
        'The public pages use full-bleed colour, and the laptop and phone frames show the app for a fictional club. The club tools themselves are unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.76', [
    {
      id: '2026-10-25-bolder-public-site',
      date: '2026-10-25',
      kind: 'change',
      title: 'Bolder pictures on the public site',
      summary:
        'The public home, features, and co-pilot pages show the app on larger device frames with stronger colour. The club tools themselves are unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.75', [
    {
      id: '2026-10-21-programmes-on-the-phone',
      date: '2026-10-21',
      kind: 'new',
      title: 'Write the programme on your phone',
      summary:
        'Coaches open Programmes and paste several sessions in the week — Monday, Tuesday, and the rest — moving between them before Publish week sends them together. The phone keeps each draft if the app closes, and the same full text shows in the admin program deck.',
      audience: 'both',
    },
  ]),
  ...release('1.74', [
    {
      id: '2026-10-21-coach-tab-for-every-coach',
      date: '2026-10-21',
      kind: 'change',
      title: 'Every coach gets the Coach tab',
      summary:
        'Head coaches, squad coaches, and assistant coaches see Coach on the phone’s bottom bar. Parents do not. Programmes, including Terms used, sit inside Coach. An old Programmes link opens there.',
      audience: 'both',
    },
  ]),
  ...release('1.73', [
    {
      id: '2026-10-21-swap-coach-on-the-phone',
      date: '2026-10-21',
      kind: 'new',
      title: 'Swap a session from your phone',
      summary:
        'On Coach, Today and Sessions, tap Swap coach, pick another coach, and add a short note. A head coach’s swap happens straight away. Another coach can hand a session over now or ask the other coach to accept. Swap every session you have on a day if you’re away. The session shows who is covering, and the other coaches get a notice, an email, and a phone alert.',
      audience: 'both',
    },
  ]),
  ...release('1.72', [
    {
      id: '2026-10-21-session-full-and-walk-ins',
      date: '2026-10-21',
      kind: 'change',
      title: 'A lane holds 7 swimmers',
      summary:
        'Booking stops at 7 swimmers a lane. When a session is full, the phone says Session full. A coach can still mark someone who turns up without a booking as a walk-in.',
      audience: 'both',
    },
    {
      id: '2026-10-21-copilot-saves-the-note',
      date: '2026-10-21',
      kind: 'fix',
      title: 'Co-pilot keeps the note if the mic or the connection drops',
      summary:
        'A head coach can save a voice note with or without a coach profile. If the phone loses the transcription or the save takes too long, the note stays on the phone with a Retry.',
      audience: 'both',
    },
    {
      id: '2026-10-21-programme-terms-and-meets',
      date: '2026-10-21',
      kind: 'change',
      title: 'Programme shorthand and the meets list',
      summary:
        'Coaches see the meaning of shorthand under a session programme, and a head coach can edit those descriptions in the phone app. Parents, swimmers, and coaches see upcoming meets with the date, venue, and entry deadline.',
      audience: 'both',
    },
  ]),
  ...release('1.71', [
    {
      id: '2026-10-21-head-coach-stays-in-the-phone-app',
      date: '2026-10-21',
      kind: 'fix',
      title: 'Head coach sees every session in the phone app',
      summary:
        'Take the roll stays in the phone app. A head coach sees every squad’s sessions, today’s check-in, and the programme, even without a separate coach profile. Co-pilot saves the note straight away and fills in the tags when the connection catches up.',
      audience: 'both',
    },
  ]),
  ...release('1.70', [
    {
      id: '2026-10-20-smoke-check-names-the-failure',
      date: '2026-10-20',
      kind: 'fix',
      title: 'A failed production check now says which step stopped',
      summary:
        'The check that runs after a production publish used to stop without saying why. It now names the page it was looking at. Nothing changes in the parent app.',
      audience: 'admin',
    },
  ]),
  ...release('1.69', [
    {
      id: '2026-10-20-security-behind-the-scenes',
      date: '2026-10-20',
      kind: 'change',
      title: 'Security improvements behind the scenes',
      summary:
        'Session numbers and personal bests are checked against who is signed in. Booking a session and reading times works the same way as before.',
      audience: 'both',
    },
  ]),
  ...release('1.68', [
    {
      id: '2026-10-19-attendance-after-session-end',
      date: '2026-10-19',
      kind: 'fix',
      title: 'Auto-absent waits until the session has finished',
      summary:
        'Unmarked swimmers are marked absent 15 minutes after the session ends, not 30 minutes after it starts. A 6:00–7:45 am session stays open through 8:00 am. Coaches can change an auto-absent mark with Here or Away, including on a phone, and can update the last 14 days. Club admins are not limited to that window. Each change records who made it.',
      audience: 'admin',
    },
    {
      id: '2026-10-19-coach-pwa-and-password',
      date: '2026-10-19',
      kind: 'new',
      title: 'Coach tools stay in the parent app, and password change is on the profile',
      summary:
        'A coach who has phone coach tools turned on gets a Coach tab: today’s check-in, their sessions, and weekly programs, without leaving the parent app. Everyone else sees the same app as before. My profile, the home photo, and the dashboard link open account and password. You can change the password while signed in, or email yourself a reset link.',
      audience: 'both',
    },
  ]),
  ...release('1.67', [
    {
      id: '2026-10-18-program-publish-notify',
      date: '2026-10-18',
      kind: 'new',
      title: 'Duty coaches hear when a program week is published',
      summary:
        'Publishing a program week, or saving a real change to a week that is already published, tells each session’s duty coach. They get an in-app notice and a push, and an email when they have an address. A draft save stays quiet. Relief cover is included, and a session with no duty coach falls back to the squad primary.',
      audience: 'admin',
    },
  ]),
  ...release('1.66', [
    {
      id: '2026-10-16-snz-junior-opens-removed',
      date: '2026-10-16',
      kind: 'fix',
      title: 'Junior 12/U Opens is off the calendar',
      summary:
        'SNZ Opens (NAC) — Junior 12/U is not on Swimming NZ’s published calendar, so unused copies are removed. A copy that already has results, a file, or notes written for it stays.',
      audience: 'both',
    },
  ]),
  ...release('1.65', [
    {
      id: '2026-10-16-spookfest-name',
      date: '2026-10-16',
      kind: 'fix',
      title: 'The 1 November meet is named Spookfest',
      summary:
        'The WASC club meet on Sunday 1 November 2026 is shown as Spookfest. Entries close on 25 October. The old “Club hosted L1 & 2 LC meet” title is no longer on the calendar.',
      audience: 'both',
    },
  ]),
  ...release('1.64', [
    {
      id: '2026-10-16-spookfest-and-sample-meets',
      date: '2026-10-16',
      kind: 'fix',
      title: 'Spookfest is 1 November, and sample meets are gone',
      summary:
        'WASC Spookfest is Sunday 1 November 2026 at Whangārei Aquatic Centre. Entries close on 25 October, shown on their own. Sample meets no longer appear on any club calendar: October Training Camp, WASC Time Trial, Development Squad Invite, and Whangārei Winter Championships.',
      audience: 'both',
    },
  ]),
  ...release('1.63', [
    {
      id: '2026-10-15-fundraiser-donations',
      date: '2026-10-15',
      kind: 'new',
      title: 'Clubs can take donations from the parent app',
      summary:
        'Donations stay off until a treasurer turns them on under Finance → Donations. A club can run more than one campaign, with dates, a colour, suggested amounts, and a goal. Parents only see campaigns that are on and inside their dates. Payments are listed with a CSV export.',
      audience: 'both',
    },
  ]),
  ...release('1.62', [
    {
      id: '2026-10-14-xero-contact-link-only',
      date: '2026-10-14',
      kind: 'fix',
      title: 'Xero contacts are linked, not overwritten',
      summary:
        'Billing contacts already in Xero are linked and left as the treasurer edited them. The app does not create a second contact or change the email, name, phone, or address on an existing one. The dashboard no longer alerts on those old contact-update failures.',
      audience: 'admin',
    },
  ]),
  ...release('1.61', [
    {
      id: '2026-10-13-swimmers-filter-parents',
      date: '2026-10-13',
      kind: 'fix',
      title: 'Swimmers list hides parents',
      summary:
        'On People, Swimmers now lists only people who have the swimmer role and do not have the parent role. Someone who has both stays off that list. Admins see a Needs fixing note with a link to each profile, so those roles can be corrected by hand.',
      audience: 'admin',
    },
  ]),
  ...release('1.60', [
    {
      id: '2026-10-12-spots-left-alert',
      date: '2026-10-12',
      kind: 'new',
      title: 'A note when a session has 3 spots left',
      summary:
        'The first time a session drops to 3 open spots, families who can book it and are not already in get a notice in the parent app, a phone alert if they have allowed them, and an email. Brothers and sisters in the same family are listed on one note. It is not sent again if the number goes back up, and it drops off once the session is full or they book. Clubs can turn this off or change the number in Settings. It stays on for WASC.',
      audience: 'both',
    },
  ]),
  ...release('1.59', [
    {
      id: '2026-10-12-archived-swimmer-logins',
      date: '2026-10-12',
      kind: 'fix',
      title: 'Archived swimmers drop off the login-move list',
      summary:
        'People → Login emails no longer lists a swimmer who still has an email when that swimmer profile or their club membership is archived. They no longer count in the “still hold a sign-in” number. Show archived stays off unless you turn it on. Archived profiles are left as they are.',
      audience: 'admin',
    },
  ]),
  ...release('1.58', [
    {
      id: '2026-10-11-parent-app-login',
      date: '2026-10-11',
      kind: 'fix',
      title: 'Parents land in the parent app',
      summary:
        'Emails and sign-in links for parents now open the parent app. If a parent opens the committee site, they see a page that takes them to the parent app, with no committee menu. Committee and coaches still use the committee site.',
      audience: 'both',
    },
  ]),
  ...release('1.57', [
    {
      id: '2026-10-11-shared-session-label',
      date: '2026-10-11',
      kind: 'change',
      title: 'Shared sessions name both squads',
      summary:
        'On the phone app, a Development session that Performance swimmers can book shows Development · Performance in the session list and on Coming up. Parents and coaches see the same label. Tuesday and Thursday morning Performance sessions stay labelled Performance. Who can book each session is unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.56', [
    {
      id: '2026-10-11-home-one-card',
      date: '2026-10-11',
      kind: 'fix',
      title: 'Home shows each club event once',
      summary:
        'A notice and a maramataka entry for the same event now show as one card, with the dates kept. Tap More to read the rest of a shortened message. Coach-only reminders stay off the parent home.',
      audience: 'parents',
    },
  ]),
  ...release('1.55', [
    {
      id: '2026-10-11-hero-video-v3',
      date: '2026-10-11',
      kind: 'fix',
      title: 'Homepage hero film picture is cleaned up',
      summary:
        'The public marketing film uses a new picture with the same timing. Captions and sound are unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.54', [
    {
      id: '2026-10-09-migration-history',
      date: '2026-10-09',
      kind: 'repair',
      title: 'Database update history lines up again',
      summary:
        'The club database update list matches the files in the app again. Meet pages are unchanged, including the Swimming NZ Short Course link.',
      audience: 'admin',
    },
  ]),
  ...release('1.53', [
    {
      id: '2026-10-08-committee-member-role',
      date: '2026-10-08',
      kind: 'new',
      title: 'Committee member role',
      summary:
        'Add someone as Committee member when their committee job is not known yet. They can open the admin app, see the dashboard, and send login emails. Named jobs such as treasurer and secretary keep their own tools. Change them to a named role from the same role list.',
      audience: 'admin',
    },
  ]),
  ...release('1.52', [
    {
      id: '2026-10-08-password-reset-link',
      date: '2026-10-08',
      kind: 'fix',
      title: 'Password reset email opens a set-password page',
      summary:
        'Forgot password emails open a page where you tap Continue, choose a new password, and stay signed in. The link works from a mail app, even if the phone app is installed. An expired or already-used link explains that and offers a new email.',
      audience: 'both',
    },
  ]),
  ...release('1.51', [
    {
      id: '2026-10-08-hero-autoplay',
      date: '2026-10-08',
      kind: 'fix',
      title: 'Homepage hero film starts on its own',
      summary:
        'The public marketing film starts muted as soon as the page opens, with captions. If the browser blocks that, a Play button stays on the poster. Unmute still turns the sound on.',
      audience: 'both',
    },
  ]),
  ...release('1.50', [
    {
      id: '2026-10-08-hero-captions',
      date: '2026-10-08',
      kind: 'new',
      title: 'Homepage hero film shows captions while muted',
      summary:
        'The public marketing film shows captions while the sound is off. Unmute hides them. Mute brings them back.',
      audience: 'both',
    },
  ]),
  ...release('1.49', [
    {
      id: '2026-10-07-performance-shared-slots',
      date: '2026-10-07',
      kind: 'fix',
      title: 'Performance swimmers book each time once',
      summary:
        'Performance swimmers see each shared training time once. They book the Development session for those times, plus Tuesday and Thursday morning Performance. Booking the same time in the same pool twice is blocked. Development swimmers are unchanged. Coaches see one roll for that time, with each swimmer listed once.',
      audience: 'both',
    },
  ]),
  ...release('1.48', [
    {
      id: '2026-10-07-not-usual-session',
      date: '2026-10-07',
      kind: 'new',
      title: "Can't come: Not my usual session",
      summary:
        "When a parent says a swimmer can't come, they can choose Not my usual session. Coaches see that reason on the session roll, and it is counted with the other can't-come reasons in attendance statistics.",
      audience: 'both',
    },
  ]),
  ...release('1.47', [
    {
      id: '2026-10-08-bulk-archive-guard',
      date: '2026-10-08',
      kind: 'fix',
      title: 'Bulk archive will not hide current swimmers',
      summary:
        'On People, moving a selection to Archived asks you to confirm how many people you picked. If any of them have an active squad, sessions, or a login, the archive stops and nothing is hidden. Those are current members. Open the person and use Merge for a true duplicate.',
      audience: 'admin',
    },
  ]),
  ...release('1.46', [
    {
      id: '2026-10-07-manages-own-account-live',
      date: '2026-10-07',
      kind: 'new',
      title: 'Manages own account is in this release',
      summary:
        'An admin can turn on Manages own account so an under-18 swimmer signs in to the phone app on their own login. The setting stays off until it is turned on. The database column for it is already in place.',
      audience: 'admin',
    },
  ]),
  ...release('1.45', [
    {
      id: '2026-10-07-manages-own-account',
      date: '2026-10-07',
      kind: 'new',
      title: 'Manages own account',
      summary:
        'On a swimmer’s profile, turn on Manages own account when an under-18 swimmer should sign in to the phone app on their own login. They do not need a coach role, and they are not using a parent login. The setting is off until an admin turns it on.',
      audience: 'admin',
    },
  ]),
  ...release('1.44', [
    {
      id: '2026-10-07-signin-follows-role',
      date: '2026-10-07',
      kind: 'change',
      title: 'Sign-in follows the login role',
      summary:
        'A parent, secretary, coach, committee member, or adult swimmer can sign in even when the date of birth is under 18 or was typed in as a placeholder. A swimmer under 18 who only has the swimmer role still uses a parent login. One login can hold parent and secretary together: the phone app still shows their children, and the admin app still has the secretary permissions.',
      audience: 'both',
    },
    {
      id: '2026-10-07-suspicious-dates-of-birth',
      date: '2026-10-07',
      kind: 'new',
      title: 'Dates of birth that look like placeholders',
      summary:
        'People → Check dates of birth lists profiles whose birthday is 1 January, or a year that cannot be a real age. Replace those with the real date when you have it. A blank date is left alone.',
      audience: 'admin',
    },
    {
      id: '2026-10-07-junior-coach-login',
      date: '2026-10-07',
      kind: 'new',
      title: 'Junior coach phone-app login',
      summary:
        'On a person’s profile, or from People, assign the junior coach role and an email. Any login with that role can use the phone app while under 18, including a junior coach you add later. They get coaching-day notices. They stay listed as a swimmer on the parent login.',
      audience: 'admin',
    },
  ]),
  ...release('1.43', [
    {
      id: '2026-10-07-public-hero-promo',
      date: '2026-10-07',
      kind: 'new',
      title: 'Public site hero promo video is live',
      summary:
        'The public marketing homepage plays the approved promo film in the hero.',
      audience: 'both',
    },
  ]),
  ...release('1.42', [
    {
      id: '2026-10-07-snz-ledger-lock-live',
      date: '2026-10-07',
      kind: 'fix',
      title: 'Swimming NZ results list lock matches on staging and production',
      summary:
        'Staging and production use the same lock on the Swimming NZ results list. Only the club system can read and update that list.',
      audience: 'admin',
    },
  ]),
  ...release('1.41', [
    {
      id: '2026-10-07-snz-ledger-lock-staging-matches-production',
      date: '2026-10-07',
      kind: 'fix',
      title: 'Swimming NZ results list lock matches on staging and production',
      summary:
        'Staging and production use the same lock on the Swimming NZ results list. Only the club system can read and update that list.',
      audience: 'admin',
    },
  ]),
  ...release('1.40', [
    {
      id: '2026-10-07-snz-ledger-lock-production',
      date: '2026-10-07',
      kind: 'fix',
      title: 'Swimming NZ results list lock is on production',
      summary:
        'Production now serves the same Swimming NZ results-list lock as staging. Only the club system can read and update that list.',
      audience: 'admin',
    },
  ]),
  ...release('1.39', [
    {
      id: '2026-10-06-snz-community-files-rls',
      date: '2026-10-06',
      kind: 'fix',
      title: 'Swimming NZ results list matches production lock',
      summary:
        'The list of Swimming NZ result files on staging is no longer readable with the public app key. It now matches production: only the club system can read and update that list.',
      audience: 'admin',
    },
  ]),
  ...release('1.38', [
    {
      id: '2026-10-06-paid-fee-notices',
      date: '2026-10-06',
      kind: 'fix',
      title: 'Paid fees clear from app notices',
      summary:
        'When a fee or invoice is paid, it now clears from app notices automatically. Unpaid and part-paid amounts still show until the balance is gone.',
      audience: 'parents',
    },
  ]),
  ...release('1.37', [
    {
      id: '2026-10-06-login-transfer-auth-phone',
      date: '2026-10-06',
      kind: 'fix',
      title: 'Moving a swimmer login still finishes when the sign-in mobile cannot move',
      summary:
        'Create parent and move login no longer undoes the whole move when the mobile cannot be put on the parent sign-in. The email still moves, the physical address is still copied, and the mobile is saved on the parent and taken off the swimmer. If the sign-in itself cannot take that number, the success note says a text-message code may still reach the previous sign-in.',
      audience: 'admin',
    },
  ]),
  ...release('1.36', [
    {
      id: '2026-10-05-login-transfer-workspace',
      date: '2026-10-05',
      kind: 'change',
      title: 'See both profiles when a swimmer login moves to a parent',
      summary:
        'People → a swimmer → Transfer this sign-in shows the swimmer and the parent together: email, mobile (including a Fastlane mobile), and physical address. Email and mobile still leave the swimmer. A blank mobile is allowed. The address can be copied onto the parent and stays on the swimmer. A preview shows both profiles after you submit, so you do not have to open the other person to check.',
      audience: 'admin',
    },
  ]),
  ...release('1.35', [
    {
      id: '2026-10-05-create-parent-without-mobile',
      date: '2026-10-05',
      kind: 'fix',
      title: 'Create a parent from a swimmer login without a mobile',
      summary:
        'People → a swimmer → Create parent from this login no longer asks for a mobile. The parent receives the swimmer’s email, mobile when there is one, and physical address. The email and mobile come off the swimmer. The swimmer is left with a placeholder email and does not need a mobile. The physical address stays on the swimmer as well.',
      audience: 'admin',
    },
  ]),
  ...release('1.34', [
    {
      id: '2026-10-05-move-mobile-with-parent-login',
      date: '2026-10-05',
      kind: 'fix',
      title: 'A swimmer’s mobile moves with the parent login',
      summary:
        'Create parent from this login, and Move login to parent, copy a mobile on the swimmer onto the parent and clear it from the swimmer. Under-18 swimmers can be saved without a mobile. Parents, coaches, and other adults still need one.',
      audience: 'admin',
    },
  ]),
  ...release('1.33', [
    {
      id: '2026-10-05-session-capacity-alerts',
      date: '2026-10-05',
      kind: 'fix',
      title: 'Capacity alerts only when a session is nearly full',
      summary:
        'Today’s deck shows a session capacity card when bookings reach about 80% of the lane limit. Twelve of fourteen is nearly full. A full session, including a waitlist, says at capacity. Quiet sessions stay off the deck.',
      audience: 'both',
    },
  ]),
  ...release('1.32', [
    {
      id: '2026-10-04-adult-signin-and-create-parent',
      date: '2026-10-04',
      kind: 'fix',
      title: 'Adult sign-in, and create a parent from a swimmer login',
      summary:
        'A committee or coach sign-in is no longer stopped because a date of birth is under 18. A person who is not a swimmer is not stopped by that date either. A swimmer under 18 still uses a parent login. When that swimmer is holding the parent’s email and the parent is not in the club yet, People → the swimmer → Create parent from this login adds the parent with a name, mobile, and adult date of birth, links them, and moves the sign-in. Adding a guardian any other way still needs an email.',
      audience: 'admin',
    },
  ]),
  ...release('1.31', [
    {
      id: '2026-10-04-committee-timetable',
      date: '2026-10-04',
      kind: 'change',
      title: 'Weekly training times and pools',
      summary:
        'Tuesday, Thursday and Friday morning Performance, and Friday morning Development, run 6:00–7:45 am. Monday afternoon Performance and Development are 4:00–5:30 pm in the wave pool. Junior is in the competition pool on Monday 5:30 pm, Wednesday 6:30 pm and Thursday 5:30 pm, and in the wave pool on Tuesday 4:00 pm. Wednesday Fitness is 6:30–7:30 pm in the competition pool. Monday 4:30 pm Fitness and Thursday 4:30 pm Fitness stay in the wave pool.',
      audience: 'both',
    },
  ]),
  ...release('1.30', [
    {
      id: '2026-10-04-move-login-to-guardian',
      date: '2026-10-04',
      kind: 'fix',
      title: 'Move a child’s sign-in onto their parent',
      summary:
        'If a parent’s email was saved on a swimmer, People → Edit can move that sign-in onto the parent. Clearing the email box does not do this. Login emails skip those swimmers until the login sits on the parent.',
      audience: 'admin',
    },
    {
      id: '2026-10-04-junior-thursday-competition',
      date: '2026-10-04',
      kind: 'change',
      title: 'Junior Thursday 5:30 pm is in the competition pool',
      summary:
        'Junior swimmers have Thursday 5:30–6:30 pm in the competition pool, on the Junior programme only. Monday 5:30–6:30 pm Junior in the wave pool is unchanged. Monday 4:30–5:30 pm stays Fitness in the wave pool. Thursday 4:30 pm Fitness is unchanged.',
      audience: 'both',
    },
  ]),
  ...release('1.29', [
    {
      id: '2026-10-04-household-child-profile',
      date: '2026-10-04',
      kind: 'new',
      title: 'Each child has their own profile in the parent app',
      summary:
        'A household with more than one swimmer picks the child from the header. Sessions, meets, fees, and the shop then follow that child and their squad.',
      audience: 'parents',
    },
    {
      id: '2026-10-04-meet-theme-banner',
      date: '2026-10-04',
      kind: 'new',
      title: 'The app follows the meet that is on now',
      summary:
        'While a meet is current, the header uses that meet’s colours. The banner uses the venue photo or the meet logo. Finished meets drop off the campaign list.',
      audience: 'both',
    },
  ]),
  ...release('1.28', [
    {
      id: '2026-10-04-shared-meet-form',
      date: '2026-10-04',
      kind: 'new',
      title: 'One form for a meet on the calendar and the website',
      summary:
        'Staff add a meet once, on the public Meets and News page or on the admin calendar. Pictures, site sheets, and PDFs upload once. Tick Publish to public website to show that meet and its files on the public page. Untick hides the public row and keeps the calendar meet and the files.',
      audience: 'both',
    },
  ]),
  ...release('1.27', [
    {
      id: '2026-10-04-nz-school-holidays',
      date: '2026-10-04',
      kind: 'new',
      title: 'School holidays on the club calendar',
      summary:
        'Refresh holidays now includes Ministry of Education term breaks for 2025–2028, plus the summer break where the next year’s start is published. Training is not cancelled.',
      audience: 'both',
    },
  ]),
  ...release('1.26', [
    {
      id: '2026-10-04-parent-feedback-owner-push',
      date: '2026-10-04',
      kind: 'new',
      title: 'Phone alert when a parent sends feedback',
      summary:
        'The platform owner gets a notification when a parent sends feedback. The lock screen shows the topic only. If nobody from the club has replied after 4 hours, one reminder is sent. It does not repeat, and it is skipped once someone replies or the item is reviewed or archived. On iPhone, open the home-screen app and tap Enable once so those alerts can arrive.',
      audience: 'admin',
    },
  ]),
  ...release('1.25', [
    {
      id: '2026-10-04-import-platform-owner-api',
      date: '2026-10-04',
      kind: 'change',
      title: 'Only the platform owner can run Import',
      summary:
        'Roster, phone, and Fastlane CSV import stays closed for committee, coaches, parents, and swimmers. The import API rejects those roles as well.',
      audience: 'admin',
    },
  ]),
  ...release('1.24', [
    {
      id: '2026-09-23-import-platform-owner',
      date: '2026-09-23',
      kind: 'change',
      title: 'Import is limited to the platform owner',
      summary:
        'The Import button on the left rail is hidden for committee and coaches. Opening Import directly is blocked unless you are the platform owner.',
      audience: 'admin',
    },
  ]),
  ...release('1.23', [
    {
      id: '2026-09-23-child-uses-parent-login',
      date: '2026-09-23',
      kind: 'change',
      title: 'A swimmer under 18 uses a parent login',
      summary:
        'A child does not get their own sign-in. App access for a parent is separate from email. Sharing a billing household does not open the child’s profile. Health and safety notes sit on their own, and only the people chosen as readers can see a note.',
      audience: 'both',
    },
    {
      id: '2026-09-23-trial-session-and-space',
      date: '2026-09-23',
      kind: 'new',
      title: 'Book a trial onto a session, and see two weeks of space',
      summary:
        'Staff can start a trial and book it onto a session in the next two weeks, or start the trial without a session. Add current swimmer does not start a trial. Parents see the next two weeks, including spots left.',
      audience: 'both',
    },
  ]),
  ...release('1.22', [
    {
      id: '2026-09-22-add-member-window-stays',
      date: '2026-09-22',
      kind: 'fix',
      title: 'Add member stays open when you leave the window',
      summary:
        'Opening another browser tab, minimising, or switching to another app no longer closes Add member. The form and what you typed are still there when you come back.',
      audience: 'admin',
    },
  ]),
  ...release('1.21', [
    {
      id: '2026-09-22-home-setup-placeholders',
      date: '2026-09-22',
      kind: 'fix',
      title: 'Parent home hides empty setup steps',
      summary:
        'The phone home screen no longer shows a club setup card just because a fee is due, or a standing how-to PDF. Invoices stay under Club news. The setup card appears only when Fastlane registration is still outstanding.',
      audience: 'parents',
    },
  ]),
  ...release('1.20', [
    {
      id: '2026-09-21-feedback-thread-window',
      date: '2026-09-21',
      kind: 'change',
      title: 'Feedback conversations open in their own window',
      summary:
        'Tap a conversation on the phone app — including a home notice — or in the staff inbox, and it opens full-screen. Back or Close returns to the feedback list so longer chats are easier to read.',
      audience: 'both',
    },
  ]),
  ...release('1.19', [
    {
      id: '2026-09-21-import-keeps-squad',
      date: '2026-09-21',
      kind: 'fix',
      title: 'Import no longer moves people back a squad',
      summary:
        'Re-importing Fastlane or a roster CSV updates SNZ details but leaves the current squad alone. New swimmers still get the squad from the file. A re-import also no longer resets induction to paperwork or the join date for people already in the club. Use Squads to move someone.',
      audience: 'admin',
    },
  ]),
  ...release('1.18', [
    {
      id: '2026-09-21-feedback-pickup-replies',
      date: '2026-09-21',
      kind: 'new',
      title: 'Pick up feedback and reply in the app',
      summary:
        'Anyone who picks up a parent feedback item can reply in the thread. If it is escalated, those assignees can reply too. The parent sees a notice on the phone home screen; tapping it opens that conversation so they can keep chatting.',
      audience: 'both',
    },
  ]),
  ...release('1.17', [
    {
      id: '2026-09-20-mandatory-profile-phone',
      date: '2026-09-21',
      kind: 'change',
      title: 'Every profile needs a mobile number',
      summary:
        'Create and edit forms require a New Zealand mobile on every person — swimmers, parents, coaches, and committee. Juniors can use a parent number. On People, filter Missing phone to type numbers in the table, copy parent numbers onto swimmers, or paste an email+phone CSV from Import.',
      audience: 'admin',
    },
  ]),
  ...release('1.16', [
    {
      id: '2026-09-18-committee-login-emails',
      date: '2026-09-18',
      kind: 'fix',
      title: 'Committee can send login emails',
      summary:
        'Every committee role can send first-sign-in emails from People and Login emails — president, secretary, treasurer, membership, marketing, and events. Coaches stay out. Connecting Xero and sending finance follow-ups stay with the treasurer.',
      audience: 'admin',
    },
  ]),
  ...release('1.15', [
    {
      id: '2026-09-18-secretary-president-permissions',
      date: '2026-09-18',
      kind: 'change',
      title: 'Secretary can do what the president can',
      summary:
        'Club secretary now has the same admin access as president — squads, programs, people, settings, store, and co-pilot included. Secretary extras such as sending club comms stay in place. Connecting Xero and sending finance follow-ups stay with the treasurer.',
      audience: 'admin',
    },
  ]),
  ...release('1.14', [
    {
      id: '2026-09-18-session-roll-booked-until-coach',
      date: '2026-09-18',
      kind: 'fix',
      title: 'Session roll shows booked, checked in, and can’t come',
      summary:
        'Coaches see everyone booked on that session, including can’t-come. Parent book-in stays booked until a coach checks the swimmer in at the pool — it no longer looks like they have already arrived.',
      audience: 'both',
    },
  ]),
  ...release('1.13', [
    {
      id: '2026-09-16-nz-mobile-021-six-digit',
      date: '2026-09-16',
      kind: 'fix',
      title: 'Shorter 021 mobiles save correctly',
      summary:
        'Legacy One NZ numbers like 021 386 112 (six digits after 021) are accepted again on profile and people forms. They were wrongly treated as incomplete.',
      audience: 'both',
    },
  ]),
  ...release('1.12', [
    {
      id: '2026-09-14-isaac-stay-signed-in',
      date: '2026-09-14',
      kind: 'fix',
      title: 'Staff stay signed in after a hard refresh',
      summary:
        'Isaac and other staff keep their session on marshal-swim.co.nz. After a hard refresh, Google and email sign-in stay on this site, and Checking access no longer bounces people back to login.',
      audience: 'admin',
    },
    {
      id: '2026-09-14-bulk-induction-moves',
      date: '2026-09-14',
      kind: 'new',
      title: 'Move several people along induction at once',
      summary:
        'On People, tick the people you want to update and move them together — for example everyone in Paperwork can go to Payment in one step. Committee still confirms before anything changes.',
      audience: 'admin',
    },
    {
      id: '2026-09-14-owner-only-delete',
      date: '2026-09-14',
      kind: 'change',
      title: 'Only the platform owner can permanently delete a person',
      summary:
        'Committee can still edit, hold, and archive. Permanent delete is reserved for the platform owner and always asks for a name confirmation first.',
      audience: 'admin',
    },
  ]),
  ...release('1.11', [
    {
      id: '2026-09-14-isaac-checking-access-bounce',
      date: '2026-09-14',
      kind: 'fix',
      title: 'Admin no longer bounces back to login after Checking access',
      summary:
        'After sign-in, a slow session read or missing club link no longer force-logs people out from Checking access. The app keeps the session, retries once, and shows a clear message if club access still needs granting.',
      audience: 'admin',
    },
  ]),
  ...release('1.10', [
    {
      id: '2026-09-14-isaac-supabase-proxy',
      date: '2026-09-14',
      kind: 'fix',
      title: 'Sign-in works when *.supabase.co is blocked',
      summary:
        'Admin and parent Google (and email) sign-in stay on marshal-swim.co.nz instead of opening the Supabase hostname first. That unblocks school and home networks that could not reach *.supabase.co.',
      audience: 'both',
    },
  ]),
  ...release('1.9', [
    {
      id: '2026-09-14-update-notes-rail',
      date: '2026-09-14',
      kind: 'change',
      title: 'Update notes sits on the left icon rail',
      summary:
        'Open Update notes from the left icon strip, just under Feedback. It is no longer listed under Club in the wider menu.',
      audience: 'admin',
    },
  ]),
  ...release('1.8', [
    {
      id: '2026-09-13-update-notes-versions',
      date: '2026-09-13',
      kind: 'change',
      title: 'Browse earlier and upcoming update notes',
      summary:
        'Update notes keeps the full ship log. Open Current, Previous, or Upcoming, and filter by version — v1.8, v1.7, and earlier.',
      audience: 'admin',
    },
  ]),
  ...release('1.7', [
  {
    id: '2026-09-02-login-emails',
    date: '2026-09-02',
    kind: 'new',
    title: 'Login emails board — Sent and Received',
    summary:
      'People → Login emails shows who was sent a first-sign-in email and who has signed in (Received). Committee and families get different emails. Send to everyone not yet sent from that page.',
    audience: 'admin',
  },
  {
    id: '2026-09-02-feedback-triage',
    date: '2026-09-02',
    kind: 'change',
    title: 'App help goes to committee first',
    summary:
      'Sign-in and app problems use Feedback → App help. Committee help first. Escalate to platform only for a genuine technical bug — that is when the platform owner is notified.',
    audience: 'both',
  },
  ]),
  ...release('1.6', [
  {
    id: '2026-09-01-teamline-shop',
    date: '2026-09-01',
    kind: 'new',
    title: 'Fins and paddles from Teamline in the shop',
    summary:
      'Training fins and paddles now sit in the club shop next to Arena merch. Tap a product, pick size, then Buy on Teamline — payment stays on their site.',
    audience: 'both',
  },
  {
    id: '2026-09-01-shop-size-group',
    date: '2026-09-01',
    kind: 'fix',
    title: 'Shop lists each item once, not once per size',
    summary:
      'Uniform and kit that come in several sizes show as one card. Open the product to choose size (and colour when it applies).',
    audience: 'both',
  },
  ]),
  ...release('1.5', [
  {
    id: '2026-09-01-committee-book-arrive',
    date: '2026-09-01',
    kind: 'change',
    title: 'Book a session, then check in at the pool',
    summary:
      'Families book a session in advance. When they arrive, they or the coach tap Check in. Occupancy still counts bookings so lanes stay fair.',
    audience: 'both',
  },
  {
    id: '2026-09-01-committee-sessions-meets-shop',
    date: '2026-09-01',
    kind: 'new',
    title: 'Add or cancel sessions, clearer meets, shop tidy-up',
    summary:
      'Coaches and membership can add or cancel training sessions. The meets tab leads with squad target meets and notes that other meets are welcome. Dummy shop listings are gone, and Teamline is the pointer for fins and paddles.',
    audience: 'both',
  },
  {
    id: '2026-09-01-committee-family-archive',
    date: '2026-09-01',
    kind: 'fix',
    title: 'One email, several swimmers — and archive stays archived',
    summary:
      'A parent email can cover more than one swimmer. Membership can move swimmers between squads. Archiving a swimmer sends them to Archive, not back onto the induction board. Parents can see and update first-contact details on the swimmer page.',
    audience: 'both',
  },
  ]),
  ...release('1.4', [
  {
    id: '2026-08-18-book-in-cannot-attend',
    date: '2026-08-18',
    kind: 'change',
    title: 'Book in, or tell us they cannot come',
    summary:
      "Session check-in is now book-in. Parents can book a swimmer in, or tap Can't come with a reason so coaches hear ahead of time — the same reasons are used when un-booking.",
    audience: 'both',
  },
  ]),
  ...release('1.3', [
  {
    id: '2026-08-14-copilot-phone-only',
    date: '2026-08-14',
    kind: 'change',
    title: 'Co-pilot stays on the phone',
    summary:
      'Co-pilot is for poolside voice notes, so it is no longer in the computer admin menu. Coaches still open it from the phone app.',
    audience: 'admin',
  },
  ]),
  ...release('1.2', [
  {
    id: '2026-08-14-how-to-use-guide',
    date: '2026-08-14',
    kind: 'new',
    title: 'One-page how-to guide in welcome emails',
    summary:
      'New families and staff get a one-page PDF with their welcome email — how to sign in, put the phone app on the Home Screen, and find their way around both apps.',
    audience: 'both',
  },
  ]),
  ...release('1.1', [
  {
    id: '2026-08-09-update-notes-page',
    date: '2026-08-09',
    kind: 'new',
    title: 'Update notes in admin',
    summary:
      'Club → Update notes lists what changed in the past calendar month in plain English — new features, fixes, changes, and repairs.',
    audience: 'admin',
  },
  {
    id: '2026-08-09-parent-feedback',
    date: '2026-08-09',
    kind: 'new',
    title: 'Parent feedback in the app',
    summary:
      'Parents can send quick notes from the mobile app. Committee and coaches see them in a new Feedback inbox, with simple stats on the Statistics page.',
    audience: 'both',
  },
  {
    id: '2026-08-09-meets-detail',
    date: '2026-08-09',
    kind: 'new',
    title: 'Clearer meet details for parents',
    summary:
      'The Meets screen opens a detail sheet with dates, documents, and links to the official meet page so families can act without hunting around.',
    audience: 'parents',
  },
  {
    id: '2026-08-09-statistics-leaderboards',
    date: '2026-08-09',
    kind: 'new',
    title: 'Attendance leaderboards and cancel reasons',
    summary:
      'Statistics now highlights who is attending well and why people cancel or check out, so coaches can spot patterns at a glance.',
    audience: 'admin',
  },
  {
    id: '2026-08-07-junior-coach',
    date: '2026-08-07',
    kind: 'new',
    title: 'Junior coach role',
    summary:
      'A dedicated Junior coach club role is available for staffing and access gates, separate from squad and relief coaches.',
    audience: 'admin',
  },
  {
    id: '2026-08-07-induction-emails',
    date: '2026-08-07',
    kind: 'new',
    title: 'Richer induction emails and app support link',
    summary:
      'New member emails are clearer and include a join link for the WASC App Support WhatsApp group so parents can ask for help quickly.',
    audience: 'both',
  },
  {
    id: '2026-07-09-arena-shopify',
    date: '2026-07-09',
    kind: 'new',
    title: 'Arena store orders sync into admin',
    summary:
      'Shopify Arena catalog and orders can sync into Marshal admin (WASC-filtered), including near-instant product updates via webhooks.',
    audience: 'admin',
  },
  {
    id: '2026-08-09-comms-one-step',
    date: '2026-08-09',
    kind: 'fix',
    title: 'Club news publishes in one step',
    summary:
      'Communications posts go straight to the parent app Home under Club news — write and publish, without a draft/approve maze.',
    audience: 'both',
  },
  {
    id: '2026-08-09-home-session-alerts',
    date: '2026-08-09',
    kind: 'fix',
    title: 'Home no longer stacks session alert noise',
    summary:
      'Session-style alerts were removed from the parent Home feed so Club news and real alerts stay readable.',
    audience: 'parents',
  },
  {
    id: '2026-08-07-ios-home-screen-session',
    date: '2026-08-07',
    kind: 'fix',
    title: 'Stay signed in after Add to Home Screen on iPhone',
    summary:
      'Parents who save the app to their home screen keep their login instead of being bounced out unexpectedly.',
    audience: 'parents',
  },
  {
    id: '2026-08-09-cancel-reason-timing',
    date: '2026-08-09',
    kind: 'fix',
    title: 'Check-out reasons use cancel time, not session date',
    summary:
      'Reports and stats now attribute cancel/check-out reasons to when the parent cancelled, which matches what actually happened.',
    audience: 'admin',
  },
  {
    id: '2026-07-30-poolside-auto-open',
    date: '2026-07-30',
    kind: 'fix',
    title: 'Poolside no longer blocks “All sessions”',
    summary:
      'Live-session auto-open on poolside tablets stopped trapping coaches away from the full session list.',
    audience: 'admin',
  },
  {
    id: '2026-08-09-pwa-sessions-checkin',
    date: '2026-08-09',
    kind: 'change',
    title: 'Household check-in with cancel reasons',
    summary:
      'Parents only manage check-in for their own household, can give a reason when cancelling, and sessions are filtered to the swimmer’s squad (with a one-way Performance guest path where allowed).',
    audience: 'parents',
  },
  {
    id: '2026-08-07-parent-signin',
    date: '2026-08-07',
    kind: 'change',
    title: 'Parent sign-in: password, Google, and email code',
    summary:
      'Parents set a password or use Google where enabled, with email one-time codes kept for induction-style logins when needed.',
    audience: 'parents',
  },
  {
    id: '2026-08-09-pwa-header',
    date: '2026-08-09',
    kind: 'change',
    title: 'Cleaner parent app header',
    summary:
      'Brand and club name text were removed from the PWA header so the screen feels less cluttered on a phone.',
    audience: 'parents',
  },
  {
    id: '2026-07-09-admin-google-auth',
    date: '2026-07-09',
    kind: 'change',
    title: 'Admin and parent Google sign-in',
    summary:
      'Staff and parents can sign in with Google when the club has it switched on in Supabase; email options remain as a fallback.',
    audience: 'both',
  },
  {
    id: '2026-08-01-demo-scrub',
    date: '2026-08-01',
    kind: 'repair',
    title: 'Live parent onboarding hardened; demo traces scrubbed',
    summary:
      'Demo-account cleanup and related database links were fixed so real parent onboarding is not confused with test data.',
    audience: 'admin',
  },
  {
    id: '2026-08-07-hosted-db-scripts',
    date: '2026-08-07',
    kind: 'repair',
    title: 'Hosted database migrate scripts more reliable',
    summary:
      'Tower/hosted apply scripts handle passwords and URL encoding more safely so repairs and migrations land cleanly in production.',
    audience: 'admin',
  },
  {
    id: '2026-08-07-people-induction-resend',
    date: '2026-08-07',
    kind: 'fix',
    title: 'Clearer “re-send induction” in People',
    summary:
      'The People actions menu labels re-send induction plainly so membership officers can find it without guessing.',
    audience: 'admin',
  },
  ]),
];
